import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const BrandingContext = createContext({});

// Convert HEX to HSL values string (e.g. "239 84% 67%")
function hexToHsl(hex) {
  hex = hex.replace('#', '');
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s;
  const l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Determine if a color is light or dark
function isLightColor(hex) {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6;
}

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState({ brand_name: null, logo_url: null, primary_color: null });
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const applyColor = useCallback((color) => {
    if (!color || !/^#[0-9A-Fa-f]{6}$/.test(color)) return;

    const hslValue = hexToHsl(color);
    const root = document.documentElement;

    root.style.setProperty('--primary', hslValue);
    root.style.setProperty('--accent', hslValue);
    root.style.setProperty('--ring', hslValue);
    root.style.setProperty('--chart-1', hslValue);

    // Set foreground based on brightness
    if (isLightColor(color)) {
      root.style.setProperty('--primary-foreground', '222 47% 11%');
      root.style.setProperty('--accent-foreground', '222 47% 11%');
    } else {
      root.style.setProperty('--primary-foreground', '0 0% 100%');
      root.style.setProperty('--accent-foreground', '0 0% 100%');
    }
  }, []);

  const fetchBranding = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/platform-branding`);
      const data = response.data;
      setBranding(data);
      if (data.primary_color) {
        applyColor(data.primary_color);
      }
      // Apply favicon dynamically
      if (data.favicon_url) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = `${API}${data.favicon_url}`;
      }
      // Il nome dell'app installata e il titolo seguono il brand
      if (data.brand_name) {
        document.title = data.brand_name;
        const appleTitle = document.querySelector("meta[name='apple-mobile-web-app-title']");
        if (appleTitle) appleTitle.setAttribute('content', data.brand_name.split(' by ')[0]);
      }
      const manifest = document.querySelector("link[rel='manifest']");
      if (manifest && !manifest.getAttribute('href').startsWith('/api/')) {
        manifest.href = `${API}/manifest.json`;
      }
      // Su iOS l'icona della home arriva da apple-touch-icon
      if (data.logo_url) {
        document.querySelectorAll("link[rel='apple-touch-icon']").forEach((touch) => {
          touch.href = `${API}/pwa-icon/180.png`;
        });
      }
    } catch (e) {
      console.log('Platform branding non configurato');
    }
  }, [API, applyColor]);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  const refreshBranding = useCallback(() => {
    fetchBranding();
  }, [fetchBranding]);

  const value = useMemo(() => ({ branding, refreshBranding, applyColor }), [branding, refreshBranding, applyColor]);

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => useContext(BrandingContext);
